import { Activity, Boxes, DoorOpen, Gauge, Globe, LayoutDashboard, ListChecks, MessageSquare, MessageSquareWarning, PackageSearch, Sparkles, Star, Table2, Trophy, Users, Wrench, type LucideIcon } from 'lucide-react';
import { ChartBox, chartDataset } from '../components/ChartBox';
import { KpiCard } from '../components/KpiCard';
import { routes, type RouteId } from '../components/Layout';
import { Tag } from '../components/Tag';
import type { FunnelSummaryRow, NamedCountRow, OpsSummaryResponse, OverviewResponse, StaffRow, Tone, TrendRow } from '../types';
import { asNumber, colorKpi, fmtRemain, kpiLabel, money, monthLabel, num, opsStatusLabel, opsStatusTone, pct, pct1, remainTone } from '../utils';

interface OverviewScreenProps {
  data: OverviewResponse | null;
  loading: boolean;
  opsSummary: OpsSummaryResponse | null;
  onPickStore: (store: string) => void;
}

interface ModuleLink {
  id: Exclude<RouteId, 'overview'>;
  Icon: LucideIcon;
  tone: Tone;
}

interface HealthMetric {
  label: string;
  value: string;
  sub: string;
  tone: Tone;
}

const moduleLinks: ModuleLink[] = [
  { id: 'admin', Icon: Gauge, tone: 'cyan' },
  { id: 'insights', Icon: Sparkles, tone: 'violet' },
  { id: 'online', Icon: Globe, tone: 'blue' },
  { id: 'products', Icon: Boxes, tone: 'cyan' },
  { id: 'opd', Icon: PackageSearch, tone: 'amber' },
  { id: 'funnel', Icon: ListChecks, tone: 'amber' },
  { id: 'detail', Icon: Table2, tone: 'gray' },
  { id: 'chat', Icon: MessageSquare, tone: 'blue' },
  { id: 'staff', Icon: Users, tone: 'green' },
  { id: 'quality', Icon: Trophy, tone: 'green' },
  { id: 'complaints', Icon: MessageSquareWarning, tone: 'red' },
  { id: 'warranty', Icon: Wrench, tone: 'blue' },
  { id: 'reviews', Icon: Star, tone: 'amber' },
  { id: 'tcdt', Icon: DoorOpen, tone: 'violet' },
  { id: 'status', Icon: Activity, tone: 'green' },
];

const sourceLabel = (value: unknown): string => ({
  legacy_care_leads: 'Dữ liệu lịch sử',
  telegram_agent: 'Báo cáo mới',
}[String(value || '')] || String(value || 'Nguồn khác'));

const mappingLabel = (value: unknown): string => ({
  needs_hr_mapping: 'Cần xác nhận nhân viên',
  needs_review_staff_id_collision: 'Trùng thông tin nhân viên',
}[String(value || '')] || 'Cần kiểm tra');

export function OverviewScreen({ data, loading, opsSummary, onPickStore }: OverviewScreenProps) {
  if (loading && !data) return <div className="card empty">Đang tải tổng quan hệ thống...</div>;

  // ═══ Tóm tắt Chất lượng vận hành (nguồn ops-quality, tải song song) ═══
  const opsScorecard = opsSummary?.scorecard || [];
  const opsComplaints = opsSummary?.complaints;
  const opsSla = opsSummary?.sla;
  const opsWarranty = opsSummary?.warranty || [];
  const opsTcdt = opsSummary?.tcdt;
  const opsMonth = opsSummary?.meta?.month || '';
  const kpiAvg = opsScorecard.length ? opsScorecard.reduce((a, r) => a + asNumber(r.total_kpi), 0) / opsScorecard.length : 0;
  const kpiTop = opsScorecard[0];
  const kpiBottom = opsScorecard[opsScorecard.length - 1];
  const whTotal = opsWarranty.reduce((a, r) => a + asNumber(r.total), 0);
  const whAvg = whTotal ? opsWarranty.reduce((a, r) => a + asNumber(r.avg_days) * asNumber(r.total), 0) / whTotal : 0;
  const whMiss = opsWarranty.filter(r => asNumber(r.kpi_pct) < 100).length;
  const slaBusy = asNumber(opsSla?.gate1_open) + asNumber(opsSla?.expl_overdue) + asNumber(opsSla?.review_pending);
  const warrantyByStore = new Map(opsWarranty.map(r => [r.store_code, r]));

  const stats = data?.salesStats || {};
  const sourceBreakdown = data?.sourceBreakdown || [];
  const legacySource = sourceBreakdown.find(row => row.source_system === 'legacy_care_leads');
  const telegramSource = sourceBreakdown.find(row => row.source_system === 'telegram_agent');
  const closeAudit = Object.fromEntries((data?.closeAudit || []).map(row => [String(row.store_code), row]));
  const funnelRows: FunnelSummaryRow[] = (data?.funnel || []).map(row => ({ ...row, ...(closeAudit[row.store_code] || {}) }));
  const trend: TrendRow[] = data?.trend || [];
  const staffRows: StaffRow[] = (data?.staffIdentity?.length ? data.staffIdentity : data?.staff) || [];
  const vpage = data?.vpageStaffSync || {};
  const reviewRows = data?.staffSyncReview || [];
  const industryRows: NamedCountRow[] = data?.industry || [];
  const qcRows = data?.qc || [];
  const totalCaptures = asNumber(stats.captures_total);
  const totalContact = asNumber(stats.contactable);
  const missingContact = Math.max(totalCaptures - totalContact, 0);
  const contactRate = totalCaptures ? totalContact * 100 / totalCaptures : 0;
  const storesWithData = funnelRows.filter(row => asNumber(row.captures) > 0).length;
  const storesAtRisk = funnelRows.filter(row => ['at_risk', 'breach'].includes(String(row.contact_kpi_status || ''))).length;
  const reviewTotal = asNumber(vpage.review_total);
  const sourceTotal = sourceBreakdown.reduce((sum, row) => sum + asNumber(row.captures), 0);
  const topStores = funnelRows.slice(0, 8);
  const latestPeriod = trend[trend.length - 1]?.period || '-';
  const healthMetrics: HealthMetric[] = [
    { label: 'Trạng thái dữ liệu', value: data?.meta?.live === false ? 'Bản lưu' : 'Đang hoạt động', sub: 'Đã kết nối nguồn báo cáo', tone: data?.meta?.live === false ? 'amber' : 'green' },
    { label: 'Cửa hàng có dữ liệu', value: num(storesWithData), sub: `${num(funnelRows.length)} cửa hàng được theo dõi`, tone: 'blue' },
    { label: 'Cửa hàng cần chú ý', value: num(storesAtRisk), sub: 'Tỷ lệ liên hệ chưa đạt', tone: storesAtRisk ? 'amber' : 'green' },
    { label: 'Kỳ mới nhất', value: latestPeriod, sub: `${num(trend.length)} kỳ đã ghi nhận`, tone: 'cyan' },
    { label: 'Nhân viên cần đối soát', value: num(reviewTotal), sub: `${num(vpage.staff_total)} nhân viên`, tone: reviewTotal ? 'red' : 'green' },
    { label: 'Dữ liệu cần kiểm tra', value: num(stats.qc_needs_review), sub: `${num(qcRows.length)} nhóm vấn đề`, tone: asNumber(stats.qc_needs_review) ? 'amber' : 'green' },
  ];

  return (
    <>
      <section className="overview-hero space">
        <div className="overview-copy">
          <div className="overview-kicker"><LayoutDashboard size={16} /> Tổng quan hoạt động</div>
          <h2>Đang theo dõi {num(totalCaptures)} lượt tư vấn bán hàng.</h2>
          <p>
            Theo dõi tình hình cửa hàng, chất lượng dữ liệu, khả năng liên hệ lại và kết quả chăm sóc trên cùng một trang.
          </p>
          <div className="status-strip">
            <span><b>Cập nhật:</b> {data?.meta?.refreshed_at ? new Date(data.meta.refreshed_at).toLocaleString('vi-VN') : '-'}</span>
            <span><b>Trạng thái:</b> {data?.meta?.live === false ? 'Đang dùng bản lưu gần nhất' : 'Dữ liệu đang hoạt động'}</span>
          </div>
        </div>
        <div className="overview-health">
          {healthMetrics.map(metric => (
            <div className="health-pill" key={metric.label}>
              <span>{metric.label}</span>
              <b className={`tone-${metric.tone}`}>{metric.value}</b>
              <small>{metric.sub}</small>
            </div>
          ))}
        </div>
      </section>

      <section className="module-nav space" aria-label="Điều hướng dashboard">
        {moduleLinks.map(item => {
          const route = routes.find(candidate => candidate.id === item.id);
          if (!route) return null;
          return (
            <a className="module-link" href={route.hash} key={route.id}>
              <item.Icon size={18} />
              <span><b>{route.label}</b><small>{route.sub}</small></span>
              <i className={`tone-${item.tone}`}>Mở</i>
            </a>
          );
        })}
      </section>

      <section className="card space" style={{ borderLeft: '4px solid var(--violet)' }}>
        <div className="section-title">
          <h2>🏆 Chất lượng vận hành {opsMonth ? `· ${monthLabel(opsMonth)}` : ''}</h2>
          <span>KPI · khiếu nại · SLA · bảo hành · đón tiếp — từ hệ thống ops-quality{opsSummary?.meta?.snapshot_date ? ` · snapshot ${opsSummary.meta.snapshot_date}` : ''}</span>
        </div>
        {!opsSummary ? (
          <div className="card empty">Đang tải tóm tắt chất lượng vận hành...</div>
        ) : opsSummary.error ? (
          <div className="card empty">Không tải được dữ liệu chất lượng vận hành: {opsSummary.error}</div>
        ) : (
          <>
            <div className="grid kpis" style={{ marginBottom: 14 }}>
              <KpiCard label="Điểm KPI trung bình" value={pct1(kpiAvg)} sub={kpiTop && kpiBottom ? `đầu ${kpiTop.store_code} ${pct1(kpiTop.total_kpi)} · cuối ${kpiBottom.store_code} ${pct1(kpiBottom.total_kpi)}` : 'chưa có snapshot'} tone={kpiAvg >= 100 ? 'green' : 'red'} />
              <KpiCard label="Khiếu nại tháng" value={num(opsComplaints?.total)} sub={`${num(opsComplaints?.confirmed)} phạt · ${num(opsComplaints?.waived)} gỡ · ${num(opsComplaints?.pending)} chờ`} tone={asNumber(opsComplaints?.total) ? 'red' : 'green'} />
              <KpiCard label="⏱ SLA cần xử lý" value={num(slaBusy)} sub={opsSla?.gate1_open ? `Gate 1: ${num(opsSla.gate1_open)} · gấp nhất ${fmtRemain(opsSla.gate1_min_remaining_h)}` : `${num(opsSla?.expl_overdue)} quá hạn giải trình · ${num(opsSla?.review_pending)} chờ duyệt`} tone={opsSla?.gate1_open ? remainTone(opsSla.gate1_min_remaining_h) : slaBusy ? 'amber' : 'green'} />
              <KpiCard label="Bảo hành TB" value={whAvg ? `${Math.round(whAvg * 100) / 100} ngày` : '-'} sub={whMiss ? `${num(whMiss)} kho đang rớt KPI` : 'các kho đều đạt'} tone={whMiss ? 'red' : 'green'} />
              <KpiCard label="Vi phạm đón tiếp" value={num(opsTcdt?.cnt)} sub={`${money(opsTcdt?.fine)} · ${num(opsTcdt?.confirmed)} đã xác nhận`} tone={asNumber(opsTcdt?.cnt) ? 'amber' : 'green'} />
            </div>
            {opsScorecard.length > 0 && (
              <div className="scroll">
                <table>
                  <thead><tr>
                    <th className="right">Hạng</th><th>Cửa hàng</th><th className="right">Tổng KPI</th><th>Xếp loại</th>
                    <th className="right">BH ngày TB</th><th className="right">KPI BH</th><th className="right">Khiếu nại</th><th className="right">Phạt</th>
                  </tr></thead>
                  <tbody>{opsScorecard.map(r => {
                    const wh = warrantyByStore.get(r.store_code);
                    const complaintCount = asNumber(opsComplaints?.by_store?.[r.store_code]);
                    return (
                      <tr key={r.store_code}>
                        <td className="right tnum"><b>{num(r.rank)}</b></td>
                        <td><b>{r.store_code}</b><div className="muted">{r.store_name}</div></td>
                        <td className="right tnum"><b>{pct1(r.total_kpi)}</b></td>
                        <td><Tag tone={opsStatusTone(r.status)}>{opsStatusLabel(r.status)}</Tag></td>
                        <td className="right tnum">{wh ? num(Math.round(asNumber(wh.avg_days) * 100) / 100) : <span className="muted">-</span>}</td>
                        <td className="right">{wh ? <Tag tone={asNumber(wh.kpi_pct) >= 100 ? 'green' : 'red'}>{pct1(wh.kpi_pct)}</Tag> : <span className="muted">-</span>}</td>
                        <td className="right tnum">{complaintCount ? <Tag tone="red">{num(complaintCount)}</Tag> : <span className="muted">0</span>}</td>
                        <td className="right tnum">{asNumber(r.total_penalty) ? money(r.total_penalty) : <span className="muted">0</span>}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            )}
            <div className="kpi-sub" style={{ marginTop: 8 }}>
              Chi tiết: <a href="#/quality">KPI vận hành</a> · <a href="#/complaints">Khiếu nại &amp; SLA</a> · <a href="#/warranty">Bảo hành</a> · <a href="#/reviews">Review</a> · <a href="#/tcdt">Đón tiếp</a>
            </div>
          </>
        )}
      </section>

      <section className="grid kpis">
        <KpiCard label="Tổng lượt tư vấn" value={num(totalCaptures)} sub="Ghi nhận mới và dữ liệu lịch sử" tone="blue" />
        <KpiCard label="Có liên hệ" value={num(totalContact)} sub={`${contactRate.toFixed(2)}% có thể chăm lại`} tone="green" />
        <KpiCard label="Thiếu liên hệ" value={num(missingContact)} sub="Chưa có số điện thoại hợp lệ" tone={missingContact ? 'red' : 'green'} />
        <KpiCard label="Nhu cầu mua" value={num(stats.sales_needs)} sub="Hỏi giá, build, sản phẩm" tone="cyan" />
        <KpiCard label="Dịch vụ" value={num(stats.service_needs)} sub="Bảo hành, vệ sinh, đổi trả" tone="violet" />
        <KpiCard label="Cần kiểm tra" value={num(stats.qc_needs_review)} sub="Thiếu thông tin quan trọng" tone={asNumber(stats.qc_needs_review) ? 'amber' : 'green'} />
      </section>

      <section className="grid split space">
        <div className="card">
          <div className="section-title"><h2>Tình trạng dữ liệu hiện tại</h2><span>Các chỉ số cần chú ý</span></div>
          <div className="quality">
            {healthMetrics.map(metric => (
              <div className="quality-row" key={metric.label}>
                <span>{metric.label}<small>{metric.sub}</small></span>
                <b className={`tone-${metric.tone}`}>{metric.value}</b>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Nguồn ghi nhận</h2><span>{num(sourceTotal)} lượt tư vấn</span></div>
          <div className="source-stack">
            {sourceBreakdown.map(row => {
              const count = asNumber(row.captures);
              const width = sourceTotal ? Math.max(count * 100 / sourceTotal, 3) : 0;
              return (
                <div className="source-row" key={String(row.source_system)}>
                  <div><b>{sourceLabel(row.source_system)}</b><span>{num(count)} lượt</span></div>
                  <div className="bar"><i style={{ width: `${width}%`, background: row.source_system === 'telegram_agent' ? 'var(--green)' : 'var(--blue)' }} /></div>
                </div>
              );
            })}
            {!sourceBreakdown.length && <div className="empty">Chưa có breakdown nguồn dữ liệu.</div>}
          </div>
        </div>
      </section>

      <section className="grid split space">
        <div className="card">
          <div className="section-title"><h2>Xu hướng ghi nhận theo tháng</h2><span>{trend.length} kỳ</span></div>
          <div className="chart">
            <ChartBox labels={trend.map(row => row.period)} datasets={[
              chartDataset('Lượt tư vấn', trend.map(row => asNumber(row.captures)), '#2563eb'),
              chartDataset('Có liên hệ', trend.map(row => asNumber(row.contactable)), '#059669', 'line'),
              chartDataset('Thiếu liên hệ', trend.map(row => asNumber(row.missing_contact)), '#d71920', 'line'),
            ]} />
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Chất lượng dữ liệu</h2><span>Kiểm tra tự động</span></div>
          <div className="quality">
            <div className="quality-row"><span>Tổng lượt tư vấn<small>Toàn bộ dữ liệu đang theo dõi</small></span><b>{num(totalCaptures)}</b></div>
            <div className="quality-row"><span>Có thông tin liên hệ<small>Sẵn sàng chăm sóc lại</small></span><b>{num(totalContact)} ({pct(stats.contactability_pct)})</b></div>
            <div className="quality-row"><span>Data cũ đã nạp<small>{sourceLabel(legacySource?.source_system)}</small></span><b>{num(legacySource?.captures || 0)}</b></div>
            <div className="quality-row"><span>Telegram agent<small>{sourceLabel(telegramSource?.source_system)}</small></span><b>{num(telegramSource?.captures || 0)}</b></div>
          </div>
        </div>
      </section>

      <section className="card space">
        <div className="section-title"><h2>Cửa hàng cần theo dõi</h2><span>Chọn cửa hàng để xem danh sách tư vấn</span></div>
        <div className="scroll">
          <table>
            <thead><tr><th>Cửa hàng</th><th className="right">Lượt ghé</th><th className="right">Lượt tư vấn</th><th className="right">Có SĐT</th><th className="right">Tỷ lệ xin SĐT</th><th>Mục tiêu</th><th className="right">Báo đã chốt</th><th className="right">Đơn đã đối soát</th></tr></thead>
            <tbody>{topStores.map(row => (
              <tr key={row.store_code} className="click-row" onClick={() => onPickStore(row.store_code)}>
                <td><b>{row.store_code}</b><div className="muted">{row.store_name}</div></td>
                <td className="right tnum">{num(row.visits)}</td>
                <td className="right tnum">{num(row.captures)}</td>
                <td className="right tnum">{num(row.contactable)}</td>
                <td className="right tnum">{pct(row.contactability_pct)} <span className="muted">/ {num(row.contact_target_pct)}%</span></td>
                <td><Tag tone={colorKpi(row.contact_kpi_status)}>{kpiLabel(row.contact_kpi_status)}</Tag></td>
                <td className="right tnum">{num(row.closed)}</td>
                <td className="right tnum">{pct(row.verified_pct)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      <section className="grid three space">
        <div className="card">
          <div className="section-title"><h2>Cơ cấu nhu cầu</h2><span>Mua hàng / dịch vụ</span></div>
          <div className="chart small">
            <ChartBox type="doughnut" labels={['Mua hàng', 'Dịch vụ', 'Thiếu thông tin']} datasets={[{ data: [asNumber(stats.sales_needs), asNumber(stats.service_needs), asNumber(stats.missing_info_needs)], backgroundColor: ['#2563eb', '#7c3aed', '#d97706'], borderWidth: 0 }]} options={{ cutout: '62%', scales: { x: { display: false }, y: { display: false } } } as never} />
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Ngành hàng</h2><span>Top 12</span></div>
          <div className="chart small"><ChartBox labels={industryRows.map(row => row.name)} datasets={[chartDataset('Lượt tư vấn', industryRows.map(row => asNumber(row.captures)), '#0891b2')]} options={{ indexAxis: 'y' }} /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Thông tin còn thiếu</h2><span>{qcRows.length} nhóm vấn đề</span></div>
          <div className="chart small"><ChartBox labels={qcRows.map(row => row.field)} datasets={[chartDataset('Lượt tư vấn', qcRows.map(row => asNumber(row.count)), '#d71920')]} options={{ indexAxis: 'y' }} /></div>
        </div>
      </section>

      <section className="grid split space">
        <div className="card">
          <div className="section-title"><h2>Đối soát nhân viên VPage</h2><span>Kiểm tra thông tin trước khi tính hiệu quả</span></div>
          <div className="quality">
            <div className="quality-row"><span>Nhân viên VPage<small>{num(vpage.vpage_users)} tài khoản · {num(vpage.vpage_pages)} trang</small></span><b className="tone-blue">{num(vpage.staff_total)}</b></div>
            <div className="quality-row"><span>Đã đối soát tự động<small>Khớp đúng thông tin nhân viên</small></span><b className="tone-green">{num(vpage.matched_existing)}</b></div>
            <div className="quality-row"><span>Chưa gán cửa hàng<small>Cần xác nhận nơi làm việc</small></span><b className="tone-amber">{num(vpage.staff_unmapped)}</b></div>
            <div className="quality-row"><span>Cần xử lý thủ công<small>{num(vpage.needs_hr_mapping)} cần xác nhận · {num(vpage.needs_review_staff_id_collision)} bị trùng</small></span><b className={reviewTotal ? 'tone-red' : 'tone-green'}>{num(reviewTotal)}</b></div>
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Nhân viên / nguồn ghi nhận</h2><span>{num(staffRows.length)} dòng</span></div>
          <div className="scroll">
            <table>
              <thead><tr><th>Nhân viên/nguồn</th><th>Cửa hàng</th><th className="right">Lượt tư vấn</th><th className="right">Có liên hệ</th><th className="right">Cần kiểm tra</th></tr></thead>
              <tbody>{staffRows.slice(0, 10).map(row => (
                <tr key={row.staff_key || row.name}>
                  <td><b>{row.name || '-'}</b><div className="muted">{row.staff_key || row.work_email || ''}</div></td>
                  <td>{row.store_code || '-'}</td>
                  <td className="right tnum">{num(row.captures)}</td>
                  <td className="right tnum">{num(row.contactable)}</td>
                  <td className="right tnum">{num(row.qc_needs_review)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="card">
        <div className="section-title"><h2>Danh sách nhân viên cần đối soát</h2><span>{num(reviewRows.length)} trường hợp cần xem</span></div>
        <div className="scroll">
          <table>
            <thead><tr><th>Đơn vị</th><th>Nhân viên VPage</th><th>Email</th><th>Cửa hàng đề xuất</th><th>Trạng thái</th></tr></thead>
            <tbody>{reviewRows.length ? reviewRows.map((row, index) => (
              <tr key={`${row.business_id}_${row.email}_${index}`}>
                <td><b>{String(row.business_id || '-')}</b><div className="muted">{String(row.business_name || '')}</div></td>
                <td>{String(row.effective_name || '-')}</td>
                <td className="tnum">{String(row.email || '-')}</td>
                <td>{String(row.proposed_store_short || 'chưa đủ dữ liệu')}</td>
                <td><Tag tone={row.mapping_status === 'needs_review_staff_id_collision' ? 'red' : 'amber'}>{mappingLabel(row.mapping_status)}</Tag></td>
              </tr>
            )) : <tr><td colSpan={5} className="muted">Không còn trường hợp cần đối soát.</td></tr>}</tbody>
          </table>
        </div>
      </section>
    </>
  );
}
